import { useState, useEffect, useCallback } from 'react';
import { apiGetUiPrefs, apiPutUiPrefs } from '../api';

/** Wire shape of the per-user ui-prefs payload (one JSON blob per page column). */
export interface UiPrefs {
  order: string[];
  open?: string[]; // foldable cards: ids of the OPEN cards
}

/**
 * useCardOrder(pageId, canonicalIds, enabled, foldable) — drag & drop card
 * order + (optionally) fold state for a page.
 *
 * OPTION cards déplaçables + pliables (user 2026-10-24/25):
 *  - `order`     : ids in the saved (or canonical) order — render cards from it.
 *  - `openIds`   : when foldable, ids of the OPEN cards (null until loaded —
 *                  the page then falls back to its defaultOpen).
 *  - `organizing`/`setOrganizing` : the "Organize" toggle (drag enabled).
 *  - `onDrop(draggedId, beforeId)` : live reorder while organizing.
 *  - `reset()` : back to canonical order.
 *
 * Persistence: per USER (PUT /api/auth/ui-prefs/:pageId) — each person keeps
 * their own layout. Unknown/new card ids are appended after the saved order,
 * so a new feature never breaks a saved layout. Signed-out / share pages never
 * call this hook.
 */
export function useCardOrder(pageId: string, canonicalIds: string[], enabled: boolean, foldable = false) {
  const [saved, setSaved] = useState<UiPrefs | null>(null);
  const [organizing, setOrganizing] = useState(false);
  const [liveOrder, setLiveOrder] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!enabled) { setSaved(null); return; }
    let cancelled = false;
    apiGetUiPrefs(pageId)
      .then((d: Partial<UiPrefs>) => { if (!cancelled) setSaved({ order: d.order || [], open: d.open }); })
      .catch(() => { if (!cancelled) setSaved({ order: [] }); });
    return () => { cancelled = true; };
  }, [pageId, enabled]);

  // Effective order: live edits while organizing > saved > canonical (unknown
  // ids appended so new features never break a saved layout).
  const savedOrder = saved?.order ?? null;
  const base = savedOrder
    ? [...savedOrder.filter((id) => canonicalIds.includes(id)), ...canonicalIds.filter((id) => !savedOrder.includes(id))]
    : canonicalIds;
  const order = liveOrder ?? base;

  const persist = useCallback((next: UiPrefs) => {
    setSaving(true);
    apiPutUiPrefs(pageId, next)
      .then(() => setSaved(next))
      .catch(() => { /* keep local state; the next change retries */ })
      .finally(() => setSaving(false));
  }, [pageId]);

  const onDrop = useCallback((draggedId: string, beforeId: string | null) => {
    const cur = [...(liveOrder ?? order)];
    const from = cur.indexOf(draggedId);
    if (from === -1) return;
    cur.splice(from, 1);
    const to = beforeId ? cur.indexOf(beforeId) : cur.length;
    cur.splice(to === -1 ? cur.length : to, 0, draggedId);
    setLiveOrder(cur);
    persist({ order: cur, open: saved?.open });
  }, [liveOrder, order, persist, saved]);

  const setOpenIds = useCallback((open: string[]) => {
    persist({ order: liveOrder ?? order, open });
  }, [liveOrder, order, persist]);

  const reset = useCallback(() => {
    setLiveOrder(null);
    persist({ order: canonicalIds, open: saved?.open });
  }, [canonicalIds, persist, saved]);

  return { order, openIds: foldable ? (saved?.open ?? null) : null, setOpenIds, organizing, setOrganizing, onDrop, reset, saving };
}
